import React, { useEffect, useMemo, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { AddressMismatchModal } from '../components/modals/AddressMismatchModal';
import { NowActiveOrderBanner } from '../components/now/NowActiveOrderBanner';
import { NowFeaturedCategories } from '../components/now/NowFeaturedCategories';
import { NowHeroCarousel } from '../components/now/NowHeroCarousel';
import { NowLimitedTimeDeal } from '../components/now/NowLimitedTimeDeal';
import { NowFloatingProductAd } from '../components/now/NowFloatingProductAd';
import { NowFloatingDeliveryStatus } from '../components/now/NowFloatingDeliveryStatus';
import { NowConnectionNotice } from '../components/now/NowConnectionNotice';
import { NowProductRail } from '../components/now/NowProductRail';
import { NowQuickCategories } from '../components/now/NowQuickCategories';
import { NowGoalSelector } from '../components/now/NowGoalSelector';
import { NowRxServiceEntry } from '../components/now/NowRxServiceEntry';
import { NowRecentSearches } from '../components/now/NowRecentSearches';
import { NowSavedQuickLists } from '../components/now/NowSavedQuickLists';
import { NowShopByStore } from '../components/now/NowShopByStore';
import { NowStickyCart } from '../components/now/NowStickyCart';
import { NowTopDeals } from '../components/now/NowTopDeals';
import { NowBrandsCarousel, NowNeedsCarousel } from '../components/now/NowVisualCarousels';
import { NowCampaignSliders } from '../components/now/NowCampaignSliders';
import { NowShopByVideo } from '../components/now/NowShopByVideo';
import { DoubleBanner } from '../components/home/DoubleBanner';
import { MultiBanner } from '../components/home/MultiBanner';
import { PromoBanners } from '../components/home/PromoBanners';
import { healthProducts, babyProducts } from '../data/products';
import { useStore } from '../lib/store';
import { useOnlineStatus } from '../lib/hooks/useOnlineStatus';
type LocationValue = {
  name: string;
  address?: string;
  type?: string;
} | null;
const detectedDeliveryAddress = '2214 Prince Mohammed Bin Salman Rd, Al Yasmin, Riyadh, Saudi Arabia';
export function NowHomePage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showAddressMismatch, setShowAddressMismatch] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationValue>(null);
  const cart = useStore(state => state.cart);
  const cartCount = useStore(state => state.cartCount());
  const total = useStore(state => state.cartTotal());
  const activeNowOrder = useStore(state => state.activeNowOrder);
  const isOnline = useOnlineStatus();
  const createNowOrder = useStore(state => state.createNowOrder);
  const updateNowOrderStatus = useStore(state => state.updateNowOrderStatus);
  useEffect(() => {
    if (activeNowOrder) return;
    createNowOrder('NOW-120-DEMO');
    updateNowOrderStatus('out-for-delivery');
  }, [activeNowOrder, createNowOrder, updateNowOrderStatus]);
  const nowProducts = useMemo(() => healthProducts.map((product, index) => ({
    ...product,
    expressDelivery: true,
    availabilityStatus: index === 0 ? 'unavailable' as const : 'available' as const
  })), []);
  const buyAgainProducts = useMemo(() => healthProducts.slice(0, 5).map(product => ({
    ...product,
    expressDelivery: true
  })), []);
  const runningLowProducts = useMemo(() => [...babyProducts.slice(0, 3), ...healthProducts.slice(5, 7)].map(product => ({
    ...product,
    expressDelivery: true
  })), []);
  const popularNearYou = useMemo(() => [...healthProducts.slice(2, 6), ...babyProducts.slice(0, 2)].map(product => ({
    ...product,
    expressDelivery: true,
    isBestSeller: true
  })), []);
  const featuredProducts = useMemo(() => healthProducts.slice(0, 5).map(product => ({
    ...product,
    expressDelivery: true
  })), []);
  const topDealProducts = useMemo(() => [...healthProducts.slice(0, 2).map(product => ({
    ...product,
    dealCategory: 'Vitamins' as const
  })), ...healthProducts.slice(2, 5).map(product => ({
    ...product,
    dealCategory: 'Health' as const
  })), ...babyProducts.slice(0, 3).map(product => ({
    ...product,
    dealCategory: 'Baby' as const
  }))], []);
  const hasActiveOrder = Boolean(activeNowOrder);
  const handleLocationSelect = (location: NonNullable<LocationValue>) => {
    setSelectedLocation(location);
    setLocationModalOpen(false);
    const selectedAddress = location.address ?? location.name;
    if (location.type === 'delivery' && selectedAddress !== detectedDeliveryAddress) {
      window.setTimeout(() => setShowAddressMismatch(true), 180);
    }
  };
  return <HelmetProvider data-id="element-8169" data-mp="hlwstt4s5lo5">
      <div className="min-h-screen bg-[#f7f8fb]" data-id="element-8170" data-mp="hmew39pv105k">
        <SEO title="NOW 120 | Pharmacy essentials delivered fast" data-id="element-8171" data-mp="hnq1tf2v8hzb" />
        <NowConnectionNotice isOnline={isOnline} data-id="element-8172" data-mp="mkzx3bcxrnv2" />

        <div className="hidden md:block" data-id="element-8173" data-mp="qmbit5sovoxz">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={handleLocationSelect} showAnnouncement data-id="element-8174" data-mp="qki7veb1mik6" />
        </div>

        <main className="pb-24 pt-0 md:pb-0 md:pt-[var(--header-height-desktop)]" data-id="element-8175" data-mp="15iam834ut0ws">
          <div className="md:hidden" data-id="element-8176" data-mp="1a9smeoz1yxgp">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} transparentAtTop showExperienceSwitcher activeExperience="innova" data-id="element-8177" data-mp="1aauapv52xggd" />
          </div>

          <NowHeroCarousel data-id="element-8178" data-mp="1oalgj8lfzqja" />
          <NowFeaturedCategories data-id="element-8179" data-mp="1o7pi7j5omzub" />
          <NowQuickCategories data-id="element-8180" data-mp="3kcf82zi89j7h" />
          <div className="mx-auto max-w-7xl px-4 pt-5 md:px-6" data-id="element-8181" data-mp="3kdqb2x3ummxj"><NowRxServiceEntry data-id="element-8182" data-mp="3fe5aki9166ip" /></div>
          <NowGoalSelector data-id="element-8183" data-mp="3fewxz5sh4xxw" />

          <div className="mx-auto max-w-7xl space-y-9 px-4 py-7 md:space-y-12 md:px-6 md:py-10" data-id="element-8184" data-mp="3ffoo413ay3gs">
            <NowRecentSearches data-id="element-8185" data-mp="3u42uun3uac2m" />
            {hasActiveOrder && <NowActiveOrderBanner onTrack={() => navigate('/track-order?mode=now')} data-id="element-8186" data-mp="3pbrrtbdsya5u" />}
            <NowSavedQuickLists data-id="element-8187" data-mp="6xse2s8m0gq9" />
            <NowTopDeals products={topDealProducts} data-id="element-8188" data-mp="6wqx674wx00m" />
            <NowLimitedTimeDeal data-id="element-8189" data-mp="2wgr6uh4e9lr" />
            <NowCampaignSliders data-id="element-8190" data-mp="2vf760i4mcrw" />
            <DoubleBanner data-id="element-8191" data-mp="2w6yv08lrqcw" />

            <NowProductRail title="Deal Zone" subtitle="Fast-delivery offers available near you" viewAllLink="/offers" products={nowProducts} data-id="element-8192" data-mp="gw85kwbjts0a" />

            <NowProductRail title="Buy again" subtitle="Your everyday essentials, ready to add" viewAllLink="/account/orders" products={buyAgainProducts} data-id="element-8193" data-mp="2in2ztjq3xj4w" />

            <NowProductRail title="Running low?" subtitle="Restock your pharmacy and family essentials" viewAllLink="/category/personal-care?from=now" products={runningLowProducts} data-id="element-8194" data-mp="t7io42lxf1b5" />

            <NowProductRail title="Recommended for you" subtitle="Fast-moving pharmacy picks in your area" viewAllLink="/category/medicines?from=now" products={popularNearYou} data-id="element-8195" data-mp="ofr4yd53nxv1" />

            <PromoBanners data-id="element-8196" data-mp="ffu9nso7g0r0" />
            <NowNeedsCarousel data-id="element-8197" data-mp="fgdq14y7ih2x" />
            <NowShopByStore data-id="element-8198" data-mp="i1ux2qhfgdv" />

            <NowProductRail title="Vitamins & Supplements" subtitle="Support your everyday wellness" viewAllLink="/category/vitamins-supplements?from=now" products={featuredProducts} data-id="element-8199" data-mp="iv4sckqmm08" />

            <NowBrandsCarousel data-id="element-8200" data-mp="1uu026z58e274" />
            <NowShopByVideo products={featuredProducts.slice(0, 3)} data-id="element-8201" data-mp="1zv5zla9u6dmz" />
            <MultiBanner data-id="element-8202" data-mp="nlkmqqgbwo5d" />
          </div>
        </main>

        <div className="hidden md:block" data-id="element-8203" data-mp="nl15zd2f05sx"><Footer data-id="element-8204" data-mp="nndj9cx89rzn" /></div>
        <MobileFooterNav data-id="element-8205" data-mp="ilxyllyypnhs" />
        {activeNowOrder && <NowFloatingDeliveryStatus orderId={activeNowOrder.id} status={activeNowOrder.status} estimatedDelivery={activeNowOrder.estimatedDelivery} isDelayed={activeNowOrder.isDelayed} revisedEstimatedDelivery={activeNowOrder.revisedEstimatedDelivery} onTrack={() => navigate('/track-order?mode=now')} data-id="element-8206" data-mp="imzmir12u4gx" />}
        <NowFloatingProductAd product={featuredProducts[0]} data-id="element-8207" data-mp="3ywezd7dykq0" />
        {cart.length > 0 && <NowStickyCart itemCount={cartCount} total={total} onViewCart={() => navigate('/cart?mode=now')} data-id="element-8208" data-mp="3vzt0ise6zadh" />}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={handleLocationSelect} data-id="element-8209" data-mp="3m3zpjdg4foxt" />
        <AddressMismatchModal isOpen={showAddressMismatch} selectedAddress={selectedLocation?.address ?? selectedLocation?.name ?? ''} detectedLocation={detectedDeliveryAddress} onClose={() => setShowAddressMismatch(false)} onConfirm={() => setShowAddressMismatch(false)} onChangeAddress={() => {
        setShowAddressMismatch(false);
        setLocationModalOpen(true);
      }} data-id="element-8210" data-mp="1kbtla8hixttn" />
      </div>
    </HelmetProvider>;
}